/// Renders live thunderstorms as soft, pulsing radar cells.
///
/// Visual approach:
///   - One `Graphics` instance per active storm, kept in a Map
///     keyed by storm id. Each frame we diff against
///     `session.thunderstorms` — add new, remove gone, redraw the
///     rest with current radius + phase color.
///   - "Soft" look comes from drawing three concentric rings: a
///     dim outer halo (filled, low alpha), a brighter mid ring,
///     and a thin opaque-ish edge stroke. That's cheaper than a
///     gradient sprite, scales correctly with radius, and reads
///     well over the basemap + rivers without needing a texture.
///   - Pulse + breathe come from a per-storm phase counter. We
///     advance it by `dt` each call, no Pixi tickers required —
///     keeps the layer self-contained and scrubs cleanly when
///     the time multiplier changes.
///
/// Mirrors iOS `GameScene.swift:2710-2802` (one circle per storm,
/// phase color, pulse + alpha breathe). Minor improvement: three
/// rings instead of one, so dense storms read as "weather radar"
/// rather than a single coloured disc.

import { Container, Graphics } from 'pixi.js'
import type { Thunderstorm, ThunderstormPhase } from '../game/Thunderstorm'
import { radarTheme } from './theme'

/// Pulse animation: scale oscillates between (1 - amplitude) and
/// (1 + amplitude) over one period. Matches the iOS feel.
const PULSE_PERIOD_SEC = 4
const PULSE_AMPLITUDE = 0.1
/// Alpha breathe (independent of pulse). Multiplies the per-ring
/// base alpha by a value in [1 - BREATHE_AMPLITUDE, 1].
const BREATHE_PERIOD_SEC = 3
const BREATHE_AMPLITUDE = 0.3

interface StormGfx {
  graphics: Graphics
  /// Per-storm clock (sim seconds, advanced each tick). Phase
  /// offsets come from id hash so multiple storms aren't pulsing
  /// in lockstep.
  pulsePhase: number
  breathePhase: number
}

export class WeatherLayer extends Container {
  private gfxById = new Map<string, StormGfx>()
  private pxPerNM: number

  constructor(pxPerNM: number) {
    super()
    this.pxPerNM = pxPerNM
  }

  /// Diff the live storm list against rendered nodes and animate.
  /// `dtSec` is sim-seconds since the last frame (already scaled
  /// by the time multiplier in GameSession.tick).
  update(storms: readonly Thunderstorm[], dtSec: number): void {
    const seen = new Set<string>()

    for (const storm of storms) {
      seen.add(storm.id)
      let entry = this.gfxById.get(storm.id)
      if (!entry) {
        const g = new Graphics()
        // Random phase offsets so storms don't pulse in unison.
        // Hash the id string to stay deterministic per session.
        const hash = hashId(storm.id)
        entry = {
          graphics: g,
          pulsePhase: (hash % 1000) / 1000 * PULSE_PERIOD_SEC,
          breathePhase: ((hash >> 4) % 1000) / 1000 * BREATHE_PERIOD_SEC,
        }
        this.gfxById.set(storm.id, entry)
        this.addChild(g)
      }

      entry.pulsePhase = (entry.pulsePhase + dtSec) % PULSE_PERIOD_SEC
      entry.breathePhase = (entry.breathePhase + dtSec) % BREATHE_PERIOD_SEC

      const pulseScale =
        1 +
        PULSE_AMPLITUDE *
          Math.sin((entry.pulsePhase / PULSE_PERIOD_SEC) * 2 * Math.PI)
      const breatheAlpha =
        1 -
        BREATHE_AMPLITUDE *
          (0.5 -
            0.5 * Math.cos((entry.breathePhase / BREATHE_PERIOD_SEC) * 2 * Math.PI))

      this.redraw(entry.graphics, storm, pulseScale, breatheAlpha)
    }

    // Garbage-collect storms that left the list (dissipated or
    // drifted off-world).
    for (const [id, entry] of this.gfxById) {
      if (!seen.has(id)) {
        entry.graphics.destroy()
        this.gfxById.delete(id)
      }
    }
  }

  /// Force a full re-projection (e.g., on viewport resize). Cheap;
  /// we just resize each ring in place.
  setPxPerNM(pxPerNM: number): void {
    this.pxPerNM = pxPerNM
  }

  destroy(options?: Parameters<Container['destroy']>[0]): void {
    for (const entry of this.gfxById.values()) entry.graphics.destroy()
    this.gfxById.clear()
    super.destroy(options)
  }

  private redraw(
    g: Graphics,
    storm: Thunderstorm,
    pulseScale: number,
    breatheAlpha: number,
  ): void {
    g.clear()
    g.position.set(storm.x, storm.y)

    const color = phaseColor(storm.phase)
    const radius = storm.sizeNM * this.pxPerNM * pulseScale
    if (radius <= 0) return

    // Outer halo: dim, fills the full footprint so the player sees
    // the area-of-effect at a glance even before the inner cell
    // pulses up.
    g.circle(0, 0, radius)
    g.fill({ color, alpha: 0.15 * breatheAlpha })

    // Mid ring: brighter fill at 70% radius, brings the cell weight
    // toward the centre.
    g.circle(0, 0, radius * 0.7)
    g.fill({ color, alpha: 0.3 * breatheAlpha })

    // Edge stroke: keeps the boundary readable on top of ambient
    // basemap colour without the iOS-style thick line.
    g.circle(0, 0, radius)
    g.stroke({ color, width: 1.5, alpha: 0.7 * breatheAlpha })
  }
}

function phaseColor(phase: ThunderstormPhase): number {
  switch (phase) {
    case 'forming':
      return radarTheme.weatherForming
    case 'mature':
      return radarTheme.weatherMature
    case 'dissipating':
      return radarTheme.weatherDissipating
  }
}

/// Cheap deterministic hash for the per-storm phase offset. Doesn't
/// need to be cryptographic — just stable.
function hashId(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}
